import React from 'react';
import { Monitor, Package } from 'lucide-react';
import Badge from './Badge';

/**
 * Everything an employee has ever held — assets and accessories, past and
 * present, with the period held. Shared by the employee profile (admin / IT)
 * and My Portal (the employee's own view), so both read identically.
 *
 * history — { assets: [], accessories: [] } from GET /employees/:id/item-history
 */

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : null);
const cap = (v) => String(v || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

/** "1y 2mo" / "18d" — how long the item was held. */
export function heldFor(from, to) {
  if (!from) return null;
  const end = to ? new Date(to) : new Date();
  const days = Math.max(0, Math.round((end - new Date(from)) / 86400000));
  if (days < 31) return `${days} day${days === 1 ? '' : 's'}`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  return `${Math.floor(months / 12)}y ${months % 12}mo`;
}

/** Plain-language end of a holding: returned, or why it left the register. */
function endedLabel(row) {
  if (row.still_held) return null;
  if (row.derecognized) return `${cap(row.derecognition_type) || 'Derecognized'}${row.derecognition_date ? ` on ${fmtDate(row.derecognition_date)}` : ''}`;
  if (row.action && row.action !== 'assigned') return cap(row.action);
  return 'Returned';
}

function Row({ row, kind }) {
  const Icon = kind === 'asset' ? Monitor : Package;
  const accent = kind === 'asset' ? '#60a5fa' : '#a78bfa';
  const from = fmtDate(row.assigned_date);
  const to = fmtDate(row.returned_date);
  const duration = heldFor(row.assigned_date, row.returned_date);
  const ended = endedLabel(row);

  return (
    <div className="flex items-start gap-3 p-2.5 rounded-lg"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}1f`, border: `1px solid ${accent}33` }}>
        <Icon size={12} style={{ color: accent }}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs font-semibold" style={{ color: accent }}>{row.item_number || '—'}</span>
          {row.category && <span className="text-xs" style={{ color: '#475569' }}>{cap(row.category)}</span>}
        </div>
        <div className="text-sm" style={{ color: '#e2e8f0' }}>
          {[row.name, row.brand, row.model].filter(Boolean).join(' ') || '—'}
        </div>
        <div className="text-xs" style={{ color: '#334155' }}>
          {row.serial_number ? `S/N: ${row.serial_number}` : ''}
        </div>
        {/* Period held */}
        <div className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>
          {row.still_held
            ? <>Held since <strong style={{ color: '#cbd5e1' }}>{from || 'date not recorded'}</strong>{duration ? ` · ${duration}` : ''}</>
            : from || to
              ? <>Used <strong style={{ color: '#cbd5e1' }}>{from || '—'}</strong> → <strong style={{ color: '#cbd5e1' }}>{to || '—'}</strong>{duration ? ` · ${duration}` : ''}</>
              : <span style={{ color: '#475569' }}>Period not recorded</span>}
        </div>
        {(row.condition_at_return || row.remarks) && (
          <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
            {row.condition_at_return ? `Condition back: ${row.condition_at_return}` : row.remarks}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {row.still_held
          ? <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', fontSize: 9.5 }}>Still held</span>
          : <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8', fontSize: 9.5 }}>{ended}</span>}
        {row.status && <Badge value={row.status}/>}
      </div>
    </div>
  );
}

export default function ItemHistoryList({ history, emptyMsg = 'No assets or accessories on record' }) {
  const assets = history?.assets || [];
  const accessories = history?.accessories || [];
  // Current holdings first, then most recently returned
  const sort = (rows) => [...rows].sort((a, b) => {
    if (!!a.still_held !== !!b.still_held) return a.still_held ? -1 : 1;
    const ax = new Date(a.returned_date || a.assigned_date || 0);
    const bx = new Date(b.returned_date || b.assigned_date || 0);
    return bx - ax;
  });

  if (!assets.length && !accessories.length) {
    return <div className="text-xs text-center py-3" style={{ color: '#334155' }}>{emptyMsg}</div>;
  }

  return (
    <div className="space-y-3">
      {assets.length > 0 && (
        <div>
          <div className="text-xs font-semibold mb-1.5" style={{ color: '#60a5fa' }}>Assets ({assets.length})</div>
          <div className="space-y-1.5">
            {sort(assets).map((r, i) => <Row key={`a${i}`} row={r} kind="asset"/>)}
          </div>
        </div>
      )}
      {accessories.length > 0 && (
        <div>
          <div className="text-xs font-semibold mb-1.5" style={{ color: '#a78bfa' }}>Accessories ({accessories.length})</div>
          <div className="space-y-1.5">
            {sort(accessories).map((r, i) => <Row key={`c${i}`} row={r} kind="accessory"/>)}
          </div>
        </div>
      )}
    </div>
  );
}

/** Count of everything ever held — used for the section badge. */
export const itemHistoryCount = (h) => (h?.assets?.length || 0) + (h?.accessories?.length || 0);
